import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * One menu, two platform treatments.
 *
 * Every metric that differs between the Mac and iOS treatments — row height,
 * type, icon size, radius, padding, separator inset, surface — is a custom
 * property defined in `index.css` under `.ui-menu`, switched by
 * `@media (hover: hover) and (pointer: fine)`. This component reads those
 * properties and never branches on platform itself.
 *
 * That split has to live in CSS. Reading a media query from JS means a
 * listener, a listener resolves after first paint, and the menu would flash at
 * the wrong size before correcting. It also means a capability query rather
 * than a width query, so a large tablet gets the touch treatment and a small
 * laptop gets the pointer treatment, which a breakpoint gets backwards.
 *
 * Built to carry more than the message menu: `groups` covers the three-dot
 * menu's shape too, including named groups and submenu rows.
 */

export interface MenuItem {
  key: string;
  label: string;
  /** Rendered at `--menu-icon`, leading the row. */
  icon: ReactNode;
  onSelect: () => void;
  /** Tints label and icon to the destructive red. */
  destructive?: boolean;
  /**
   * Marks the current value in a picker: biscayne fill, white label and icon.
   * Only set by UtrPicker — a command menu has no "current" row.
   */
  selected?: boolean;
  /** Draws a trailing chevron. The submenu itself is the caller's business. */
  hasSubmenu?: boolean;
  disabled?: boolean;
}

export interface MenuGroup {
  key: string;
  /**
   * Small grey group label above the group. An iOS idiom — `index.css` hides
   * it on pointer devices, where separators alone do the grouping.
   */
  label?: string;
  items: MenuItem[];
}

interface ContextMenuProps {
  /** Viewport point the menu is anchored to. null closes it. */
  anchor: { x: number; y: number } | null;
  onClose: () => void;
  groups: MenuGroup[];
  /** Optional content above the first group, e.g. the reaction picker. */
  header?: ReactNode;
  /**
   * A group rendered above the scrolling body and outside it, so it stays put
   * while the body scrolls. For a long picker whose first choice must not be
   * 90 rows above the viewport.
   */
  pinnedGroup?: MenuGroup;
  /**
   * Caps the group body and lets it scroll. Without it the menu is its natural
   * height, which is right for a handful of actions and impossible for a list.
   */
  maxBodyHeight?: number;
  /**
   * Item key to bring into view when the menu opens, centred in the body. The
   * current value of a picker, so it opens where the user already is.
   */
  scrollToKey?: string;
  /**
   * `end` pins the menu's right edge to the anchor point instead of leaving it
   * to the flip. A picker has to sit under the value it replaces, and the flip
   * only fires when the menu would leave the viewport — so a narrow menu on a
   * wide screen stays left-aligned and hangs past the row.
   */
  align?: 'start' | 'end';
  /** Extra classes on the menu surface, for a scoped variant. */
  className?: string;
  ariaLabel?: string;
}

/** Gap between the anchor point and the menu edge. */
const ANCHOR_GAP = 6;
/** Closest the menu is allowed to come to a viewport edge. */
const VIEWPORT_INSET = 8;

function MenuRow({ item, onClose }: { item: MenuItem; onClose: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-menu-key={item.key}
      disabled={item.disabled}
      aria-haspopup={item.hasSubmenu ? 'menu' : undefined}
      onClick={(e) => { e.stopPropagation(); item.onSelect(); onClose(); }}
      className={`w-full flex items-center text-left transition-colors disabled:opacity-40 ${
        item.selected ? '' : 'active:bg-black/[0.06] hover:bg-black/[0.04]'
      }`}
      style={{
        height: 'var(--menu-row-h)',
        padding: '0 var(--menu-row-pad-x)',
        gap: 'var(--menu-icon-gap)',
        background: item.selected ? '#1A6B8A' : undefined,
        color: item.selected
          ? '#FFFFFF'
          : item.destructive ? 'var(--menu-label-destructive)' : 'var(--menu-label)',
      }}
    >
      <span
        className="shrink-0 flex items-center justify-center"
        style={{
          width: 'var(--menu-icon)',
          height: 'var(--menu-icon)',
          // The destructive row tints its icon with its label, the way Mac
          // Messages does; a grey trash beside a red Delete reads as an
          // oversight. Ordinary rows take the secondary grey.
          color: item.selected
            ? '#FFFFFF'
            : item.destructive ? 'var(--menu-label-destructive)' : 'rgba(60, 60, 67, 0.6)',
        }}
      >
        {item.icon}
      </span>
      <span
        className="min-w-0 flex-1 [overflow-wrap:anywhere]"
        style={{
          fontSize: 'var(--menu-label-size)',
          lineHeight: 'var(--menu-label-leading)',
          letterSpacing: 'var(--menu-label-tracking)',
        }}
      >
        {item.label}
      </span>
      {item.hasSubmenu && (
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(60, 60, 67, 0.6)"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="shrink-0"
          style={{ width: 'var(--menu-chevron)', height: 'var(--menu-chevron)' }}
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      )}
    </button>
  );
}

/** Hairline between groups. Full bleed on iOS, inset to the label on Mac. */
function MenuSeparator() {
  return (
    <div
      style={{
        height: 1,
        marginLeft: 'var(--menu-sep-inset)',
        background: 'var(--menu-sep)',
      }}
    />
  );
}

export default function ContextMenu({
  anchor,
  onClose,
  groups,
  header,
  pinnedGroup,
  maxBodyHeight,
  scrollToKey,
  align = 'start',
  className = '',
  ariaLabel = 'Menu',
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Placement carries the anchor it was measured against. Reading it back
  // through that check is what invalidates a stale position, rather than an
  // effect that clears it: `anchor` is a fresh object every time the menu
  // opens, so identity alone says whether `placed` still describes this menu.
  // Clearing in an effect would also mean a setState in an effect body, which
  // costs a cascading render for something derivable.
  const [placement, setPlacement] = useState<
    { anchor: { x: number; y: number }; left: number; top: number; origin: string } | null
  >(null);

  const open = anchor !== null;
  const pos = placement && placement.anchor === anchor ? placement : null;

  // Measure, then flip. Preferred placement is down-and-right of the anchor. If
  // the menu would cross the right or bottom edge it flips to the other side of
  // the anchor rather than merely sliding back inside, so it never covers the
  // point the user just acted on. transform-origin follows the chosen corner,
  // so the entry animation grows out of that point.
  useLayoutEffect(() => {
    if (!anchor || !menuRef.current) return;
    const { width, height } = menuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const flipX = anchor.x + width + VIEWPORT_INSET > vw;
    const flipY = anchor.y + height + ANCHOR_GAP + VIEWPORT_INSET > vh;

    let left = align === 'end' || flipX ? anchor.x - width : anchor.x;
    let top = flipY ? anchor.y - height - ANCHOR_GAP : anchor.y + ANCHOR_GAP;

    // If it fits in neither direction, clamp. Better a covered anchor than a
    // menu with rows off-screen.
    left = Math.min(Math.max(left, VIEWPORT_INSET), Math.max(VIEWPORT_INSET, vw - width - VIEWPORT_INSET));
    top = Math.min(Math.max(top, VIEWPORT_INSET), Math.max(VIEWPORT_INSET, vh - height - VIEWPORT_INSET));

    setPlacement({
      anchor,
      left,
      top,
      origin: `${align === 'end' || flipX ? 'right' : 'left'} ${flipY ? 'bottom' : 'top'}`,
    });
  }, [anchor, align]);

  // Focus once the menu has been measured and made visible. Focusing before
  // placement would scroll the page toward a hidden element.
  //
  // With `scrollToKey` the target row takes focus rather than the first, and
  // the body is scrolled to centre it. preventScroll is what makes the centring
  // stick: a plain focus() scrolls the row to whichever edge is nearest, which
  // would leave the current value pinned to the top or bottom of the list.
  //
  // useLayoutEffect, not useEffect: this has to land before the browser paints.
  // As an effect the menu painted once at scrollTop 0 — the top of the list —
  // and only then jumped, which is the "opens at 1.0" the picker showed.
  useLayoutEffect(() => {
    if (!pos || !menuRef.current) return;
    // Matched on the dataset rather than through a selector. The keys are
    // values like "10.9", which need escaping to go into a selector at all,
    // and an escaping bug here fails silently as "no target" — which lands on
    // the untouched top of the list and looks exactly like a scroll bug.
    const target = scrollToKey
      ? Array.from(menuRef.current.querySelectorAll<HTMLElement>('[data-menu-key]'))
          .find(el => el.dataset.menuKey === scrollToKey) ?? null
      : null;
    if (target && bodyRef.current) {
      const body = bodyRef.current;
      target.focus({ preventScroll: true });
      // Rect deltas, not offsetTop. offsetTop is measured from the nearest
      // POSITIONED ancestor, and the scrolling body is static — so it resolved
      // against the fixed menu instead, carrying the pinned row's height in as
      // an error. Rects are in one coordinate space and cannot drift.
      const bodyRect = body.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      body.scrollTop +=
        (targetRect.top - bodyRect.top) - (bodyRect.height - targetRect.height) / 2;
      return;
    }
    (target ?? menuRef.current.querySelector<HTMLElement>('[role="menuitem"]'))?.focus();
  }, [pos, scrollToKey]);

  // Dismissal: a press outside, Escape, a scroll anywhere, or a resize. Scroll
  // closes rather than repositions because the anchor is a viewport point with
  // no element to follow, and a menu that drifts away from its origin is worse
  // than one that dismisses.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDismiss = () => onClose();
    // Capture phase, so this sees scrolls from any container on the page — the
    // menu's own scrolling body included, which must not dismiss it. Anything
    // inside the menu is the user reading the menu, not leaving it.
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onDismiss);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [open, onClose]);

  // Tab is trapped and cycles: there is nowhere to tab to while a menu is open.
  // Escape and an outside press are the ways out.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !menuRef.current) return;
    const items = Array.from(menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'));
    if (items.length === 0) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.shiftKey
      ? (i <= 0 ? items.length - 1 : i - 1)
      : (i === -1 || i === items.length - 1 ? 0 : i + 1);
    items[next].focus();
  };

  if (!open) return null;

  const populated = groups.filter(g => g.items.length > 0);
  const pinned = pinnedGroup && pinnedGroup.items.length > 0 ? pinnedGroup : null;
  if (populated.length === 0 && !header && !pinned) return null;

  const renderGroup = (group: MenuGroup, separated: boolean) => (
    <div key={group.key}>
      {separated && <MenuSeparator />}
      {group.label && (
        <div
          className="ui-menu-group-label"
          style={{
            padding: '8px var(--menu-row-pad-x) 4px',
            fontSize: 13,
            lineHeight: '18px',
            letterSpacing: '0.5px',
            color: 'var(--menu-group-label)',
          }}
        >
          {group.label}
        </div>
      )}
      {group.items.map(item => (
        <MenuRow key={item.key} item={item} onClose={onClose} />
      ))}
    </div>
  );

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={`ios-glass ui-menu fixed z-[1000] overflow-hidden ${className} ${pos ? 'ios-menu-in' : ''}`}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        width: 'var(--menu-width)',
        padding: 'var(--menu-pad)',
        borderRadius: 'var(--menu-radius)',
        border: '1px solid var(--menu-border)',
        boxShadow: 'var(--menu-shadow)',
        transformOrigin: pos?.origin ?? 'left top',
        // Hidden for the one frame between mount and measurement.
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {header}
      {/* Outside the scrolling body, so it stays put while the body moves. */}
      {pinned && renderGroup(pinned, !!header)}
      {/* overscroll-contain stops a flick at either end of the list from
          handing the scroll to the page behind, which on iOS would drag the
          page out from under an anchored menu. */}
      <div
        ref={bodyRef}
        className={maxBodyHeight ? 'overflow-y-auto overscroll-contain' : undefined}
        style={maxBodyHeight ? { maxHeight: maxBodyHeight } : undefined}
      >
        {populated.map((group, gi) => renderGroup(group, gi > 0 || !!header || !!pinned))}
      </div>
    </div>,
    document.body,
  );
}
